import React, { useRef, useState, useEffect } from "react";
import Game from "./features/game/Game";
import HomeScreen from "./features/lobby/HomeScreen";
import GameOver from "./features/lobby/GameOver";
import { randomName } from "./features/lobby/names";
import ChatBox from "./features/chat/ChatBox";
import KeyBindingsEditor from "./features/settings/KeyBindingsEditor";
import { formatBinding, useKeyBindings } from "./features/settings/keyBindings";
import { io } from "socket.io-client";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { updatePlayers, selectPlayers } from "./features/game/gameSlice";

// Connect to our own origin and let setupProxy.js forward /socket.io to the
// backend. Hardcoding ws://localhost:5000 breaks whenever the browser isn't on
// the same host as the server (dev container, LAN testing, any deploy).
const socket = io();

type UnitList = Number[];

type CombatEvent = { shots: any[]; blasts: any[] };
type CombatHandler = (event: CombatEvent) => void;

// One line of match chat, as the server sends it. `at` is server time in
// milliseconds and `player_id` is the seat that spoke, so the client never has
// to trust a sender's clock or take its word for who it is.
type ChatMessage = {
  id: string;
  player_id: string;
  name: string;
  text: string;
  at: number;
};

type PlayerUnits = {
  player_id: UnitList;
};

// Where this client currently sits. The server is the authority on all of it:
// it decides which match you are in, which side you hold, and whether an
// opponent has turned up yet.
type Lobby = {
  match: string;
  player_id: string | null;
  waiting: boolean;
  spectating: boolean;
  seated?: string[];
  bots?: number[];
  // The name the server actually granted us, which is not always the one we
  // asked for — it disambiguates a name someone else already holds.
  name?: string;
  // Who holds each seat. Null for an empty seat or one played by the bots.
  names?: Record<string, string | null>;
};

const SPECTATING: Lobby = {
  match: "attract",
  player_id: null,
  waiting: false,
  spectating: true,
};

type Side = { units: Record<string, { alive?: boolean }> };

// A side is out of the fight once every unit it has is dead. `alive` is absent
// until the first tick arrives, and an absent flag is not a dead unit — so a
// side nobody has heard from yet counts as standing, not as wiped.
const isWipedOut = (side?: Side) => {
  const units = Object.values(side?.units ?? {});
  return units.length > 0 && units.every((unit) => unit.alive === false);
};

function App() {
  const canvasRef = useRef(null);
  const socketRef = useRef(socket);
  // Game registers a handler here; combat events are transient effects rather
  // than state, so they go straight to the renderer instead of through redux.
  const combatRef = useRef<CombatHandler | null>(null);
  const onCombat = (event: CombatEvent) => combatRef.current?.(event);
  const dispatch = useAppDispatch();
  const players = useAppSelector(selectPlayers);
  const [lobby, setLobby] = useState<Lobby>(SPECTATING);
  // Generated once, on the first render, and theirs to edit from there.
  // Passed lazily rather than called: useState keeps only the first value, so
  // randomName() would otherwise roll a fresh name on every render and throw
  // it away — and this component re-renders on every tick.
  const [name, setName] = useState<string>(randomName);
  // What has been said in the match currently being played. Emptied on every
  // move between matches: chat belongs to one game, and carrying a line out of
  // an abandoned match into the next one would be a leak of it.
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // The newest server tick seen. Batched into the same render as the positions
  // it belongs to, so the renderer never pairs a tick with the wrong snapshot.
  const [serverTick, setServerTick] = useState<number>(-1);
  // Which chord gives which order, and the panel that changes them. Owned here
  // rather than inside the game so the on-screen hints can read them too.
  const { bindings, rebind, resetBindings } = useKeyBindings();
  const [showControls, setShowControls] = useState(false);
  // True only while the panel is actually listening for a chord. The game keeps
  // taking orders the rest of the time the panel is open.
  const [keysCaptured, setKeysCaptured] = useState(false);
  // Which match's ticks we are willing to render. Kept in a ref as well because
  // the socket handler is registered once and would otherwise close over the
  // value this render happened to see.
  const matchRef = useRef(SPECTATING.match);

  const applyLobby = (next: Lobby) => {
    // A different match is a different conversation. Cleared here rather than in
    // a handler for each way out, so leaving, being matched, and starting a bot
    // game all get it without any of them having to remember.
    if (next.match !== matchRef.current) {
      setMessages([]);
    }
    matchRef.current = next.match;
    setLobby(next);
    // Take back whatever the server granted. Asking for a name someone already
    // holds gets you a suffixed one, and the box has to show what other players
    // are actually seeing, not what we asked for.
    if (next.name) {
      setName(next.name);
    }
  };

  // `queued` is shift + right click: the point goes on the end of the unit's
  // route instead of replacing whatever it was doing.
  const emitInput = (
    playerUnits: PlayerUnits,
    x: Number,
    y: Number,
    queued: boolean = false
  ) => {
    socketRef.current.emit("input", {
      position: { x: x, y: y },
      players_dict: playerUnits,
      queued: queued,
    });
  };
  // F + left click orders a bombardment of that point.
  const emitAttack = (playerUnits: PlayerUnits, x: Number, y: Number) => {
    socketRef.current.emit("attack", {
      position: { x: x, y: y },
      players_dict: playerUnits,
    });
  };
  // The server owns unit positions, so ask it to reset rather than clearing
  // local state — the next broadcast would overwrite that anyway.
  const handleResetClick = () => socketRef.current.emit("reset");
  // The name rides along with the request to start a game rather than being
  // registered as it is typed: until a game begins it is just text in a box,
  // and reserving it earlier would hold names for people who never play.
  const handlePlayOnline = () =>
    socketRef.current.emit("play_online", { name: name });
  const handleVsBots = () => socketRef.current.emit("vs_bots", { name: name });
  const handleLeave = () => socketRef.current.emit("leave_match");
  // The message goes out and nothing is added locally: the server decides who
  // said it, when, and whether it was sent at all, and echoes it back to the
  // room. That keeps the sender's log identical to everyone else's rather than
  // showing them a line the other side never received.
  const handleSendChat = (text: string) =>
    socketRef.current.emit("chat", { text: text });

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.on("startup", (data) => {
        console.log("startup", data);
      });
      // Sent to this client alone: which match it is now in and as whom.
      socketRef.current.on("lobby", (data: Lobby) => applyLobby(data));
      // Sent to everyone in a match when its makeup changes — the moment an
      // opponent is seated, this is what ends the wait.
      socketRef.current.on(
        "match_state",
        (data: { match: string; waiting: boolean; seated: string[]; bots: number[] }) => {
          if (data.match !== matchRef.current) {
            return;
          }
          setLobby((current) => ({ ...current, ...data }));
        }
      );
      // The backlog of a match joined in progress, sent to this client alone.
      socketRef.current.on(
        "chat_history",
        (data: { match: string; messages: ChatMessage[] }) => {
          if (data.match !== matchRef.current) {
            return;
          }
          setMessages(data.messages ?? []);
        }
      );
      socketRef.current.on(
        "chat",
        (data: { match: string; message: ChatMessage }) => {
          // Same guard as the tick: a line from the match we just left can
          // still be in flight while we switch.
          if (data.match !== matchRef.current || !data.message) {
            return;
          }
          setMessages((current) => [...current, data.message]);
        }
      );
      socketRef.current.on("update_player", (data: any) => {
        // Ticks are addressed to a room, but a frame from the match we just
        // left can still be in flight while we switch. Dropping anything that
        // is not the current match keeps two games from interleaving.
        if (data.match && data.match !== matchRef.current) {
          return;
        }
        // The tick payload carries positions plus whatever combat happened on
        // that tick. Positions are state and go to redux; shots and blasts are
        // one-off events and go straight to the renderer, which owns their
        // animation clocks.
        dispatch(updatePlayers(data.players));
        // The tick this snapshot was produced on. Ticks are exactly one server
        // step apart whatever the network did to their arrival, so the renderer
        // builds its playback clock from these rather than from arrival times.
        if (typeof data.tick === "number") {
          setServerTick(data.tick);
        }
        if (data.shots?.length || data.blasts?.length) {
          onCombat({ shots: data.shots ?? [], blasts: data.blasts ?? [] });
        }
      });
      return () => {
        socketRef.current.off("connect");
        socketRef.current.off("disconnect");
        socketRef.current.off("update_player");
        socketRef.current.off("lobby");
        socketRef.current.off("match_state");
        socketRef.current.off("chat");
        socketRef.current.off("chat_history");
      };
    }
  }, []);

  const inGame = !lobby.spectating;
  // Who is left standing. Read off the same snapshot the canvas draws, so the
  // verdict can never disagree with what the player is looking at. Spectators
  // watching the attract battle get nothing: that fight is not theirs to lose,
  // and it restarts itself anyway.
  const mySide = lobby.player_id ?? "0";
  const iAmWiped = inGame && isWipedOut(players[mySide]);
  const enemyIsWiped =
    inGame &&
    Object.entries(players).some(
      ([playerId, side]) => playerId !== mySide && isWipedOut(side)
    );
  // Losing takes precedence: if a last shell traded both sides away, you did
  // not win it.
  const gameOver = iAmWiped || enemyIsWiped;
  // Whoever holds the other seat. Bots have no name of their own, and a seat
  // can be empty in a match that started before the second player arrived.
  const otherSide = mySide === "0" ? "1" : "0";
  const opponent = lobby.bots?.includes(Number(otherSide))
    ? "the bots"
    : lobby.names?.[otherSide] ?? `Player ${otherSide}`;

  return (
    <div>
      <div style={{ position: "relative", width: 800 }}>
        <Game
          canvasRef={canvasRef}
          userPlayerId={lobby.player_id ?? "0"}
          interactive={inGame}
          bindings={bindings}
          keysCaptured={keysCaptured}
          serverTick={serverTick}
          emitInput={emitInput}
          emitAttack={emitAttack}
          registerCombat={(handler: CombatHandler) => {
            combatRef.current = handler;
          }}
        />
        {!inGame && (
          <HomeScreen
            onPlayOnline={handlePlayOnline}
            onVsBots={handleVsBots}
            name={name}
            onNameChange={setName}
          />
        )}
        {/* Both routes out of here start a fresh match rather than resetting
            this one, and each is answered with a full snapshot — which is what
            clears the modal, since every unit comes back alive. */}
        {gameOver && (
          <GameOver
            won={!iAmWiped}
            onVsBots={handleVsBots}
            onPlayOnline={handlePlayOnline}
          />
        )}
      </div>
      {/* Own row. The canvas is an inline element, so loose buttons after it
          flow alongside and the first one ends up stranded to its right. */}
      {inGame && (
        <div>
          <span style={{ marginRight: 12 }}>
            {lobby.waiting
              ? `Waiting for an opponent, ${lobby.name ?? "commander"} — your units are yours to move meanwhile`
              : `${lobby.name ?? `Player ${lobby.player_id}`} vs ${opponent}`}
          </span>
          <code style={{ marginRight: 12, opacity: 0.65 }} data-match={lobby.match}>
            {lobby.match}
          </code>
          <button onClick={handleResetClick}>Reset game</button>
          <button onClick={handleLeave}>Leave match</button>
          {/* Control groups are invisible otherwise — nothing on the canvas
              says the number keys do anything. Read off the bindings rather
              than written out, so rebinding a key changes what this says. */}
          <span style={{ marginLeft: 12, opacity: 0.65 }}>
            {formatBinding(bindings.fire)} + click fires ·{" "}
            {formatBinding(bindings.setControlGroup)} + 0–9 sets a control group
            · 0–9 recalls it
          </span>
        </div>
      )}
      {/* Only in a real match. Spectators all share the attract battle, so a box
          there would be one channel holding every visitor with the page open
          rather than a chat between the two people playing. */}
      {inGame && (
        <ChatBox
          messages={messages}
          onSend={handleSendChat}
          mySide={mySide}
        />
      )}
      {/* Outside the in-game guard: a player who wants their own keys should be
          able to set them before the match rather than only during one. */}
      <div style={{ marginTop: 6 }}>
        <button onClick={() => setShowControls((open) => !open)}>
          {showControls ? "Hide controls" : "Controls"}
        </button>
      </div>
      {showControls && (
        <KeyBindingsEditor
          bindings={bindings}
          onRebind={rebind}
          onReset={resetBindings}
          onRecordingChange={setKeysCaptured}
        />
      )}
    </div>
  );
}

export default App;
